import React, { useState, useEffect } from 'react';
import { bool, func, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import classNames from 'classnames';

import { ReactComponent as HeartIcon } from './heart.svg';
import { ReactComponent as HeartFilledIcon } from './heart-filled.svg';

import css from './FavoriteButton.module.css';

const FavoriteButtonComponent = props => {
  const {
    listingId,
    position,
    currentUser,
    isOwnListing,
    history,
    onToggleFavorite,
    className,
    rootClassName,
  } = props;

  // Don't show favorite button on own listings
  if (isOwnListing) {
    return null;
  }

  const [isFavorite, setIsFavorite] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (currentUser?.id?.uuid) {
      const favorites = currentUser?.attributes?.profile?.privateData?.favorites || [];
      const listingIdString = typeof listingId === 'string' ? listingId : listingId?.uuid;
      setIsFavorite(favorites.includes(listingIdString));
    }
  }, [currentUser?.id?.uuid, listingId, currentUser?.attributes?.profile?.privateData?.favorites]);

  const handleToggleFavorite = e => {
    e.stopPropagation();
    e.preventDefault();

    // Redirect to login if not authenticated
    if (!currentUser?.id?.uuid) {
      history.push('/login', { from: history.location });
      return;
    }

    const listingIdString = typeof listingId === 'string' ? listingId : listingId?.uuid;

    // Optimistic UI update
    setIsUpdating(true);
    setIsFavorite(!isFavorite);

    // Call the toggle action
    onToggleFavorite(listingIdString, isFavorite)
      .then(() => {
        setIsUpdating(false);
      })
      .catch(e => {
        // Revert on error
        setIsFavorite(isFavorite);
        setIsUpdating(false);
        console.error('Failed to toggle favorite:', e);
      });
  };

  const classes = classNames(rootClassName || css.root, className, {
    [css.positionLeft]: position === 'left',
    [css.positionRight]: position === 'right',
  });

  const iconClasses = classNames(css.icon, {
    [css.iconFavorite]: isFavorite,
    [css.iconNotFavorite]: !isFavorite,
    [css.iconUpdating]: isUpdating,
  });

  return (
    <button
      type="button"
      className={classes}
      onClick={handleToggleFavorite}
      disabled={isUpdating}
      aria-label={isFavorite ? 'Remove from favourites' : 'Add to favourites'}
    >
      {isFavorite ? (
        <HeartFilledIcon className={iconClasses} />
      ) : (
        <HeartIcon className={iconClasses} />
      )}
    </button>
  );
};

FavoriteButtonComponent.defaultProps = {
  position: 'right',
  className: null,
  rootClassName: null,
  isOwnListing: false,
};

FavoriteButtonComponent.propTypes = {
  listingId: propTypes.uuid.isRequired,
  position: string,
  currentUser: propTypes.currentUser,
  isOwnListing: bool,
  onToggleFavorite: func.isRequired,
  className: string,
  rootClassName: string,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return {
    currentUser,
  };
};

const mapDispatchToProps = dispatch => ({
  onToggleFavorite: (listingId, isFavorite) => {
    const { toggleFavorite } = require('../../containers/FavoritesPage/FavoritesPage.duck');
    return dispatch(toggleFavorite(listingId, isFavorite));
  },
});

const FavoriteButton = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(FavoriteButtonComponent);

export default FavoriteButton;
